// src/hooks/useQuizState.ts
import { useReducer, useEffect } from 'react'
import type { Answer } from '../lib/questions'

export type QuizPhase = 'email-capture' | 'quiz' | 'submitting' | 'results' | 'hydrating'
export type Section = 'A' | 'B' | 'C' | 'D' | 'E'

export interface QuizState {
  phase: QuizPhase
  currentSection: Section
  currentQuestionIndex: number
  showSectionIntro: boolean
  answers: Map<string, Answer>
  multiSelectBuffer: Map<string, Set<string>>
  email: string
  firstName: string
  resultId: string | null
  sessionId: string | null
  submitError: string | null
}

export type QuizAction =
  | { type: 'SET_EMAIL'; email: string; firstName: string }
  | { type: 'START_QUIZ' }
  | { type: 'DISMISS_SECTION_INTRO' }
  | { type: 'ANSWER'; questionId: string; answerKey: string; answerLabel: string }
  | { type: 'NEXT_QUESTION' }
  | { type: 'PREVIOUS_QUESTION' }
  | { type: 'ENTER_SECTION'; section: Section }
  | { type: 'SUBMIT' }
  | { type: 'RESULT_READY'; resultId: string }
  | { type: 'SUBMIT_ERROR'; error: string }
  | { type: 'MULTI_SELECT_TOGGLE'; questionId: string; answerKey: string; answerLabel: string }
  | { type: 'MULTI_SELECT_CONFIRM'; questionId: string }
  | { type: 'SET_SESSION_ID'; sessionId: string }
  | { type: 'HYDRATE_SESSION_STATE'; payload: any }
  | { type: 'START_HYDRATION' }
  | { type: 'HYDRATION_FAILED' }

export const SECTION_SIZES: Record<Section, number> = { A: 7, B: 8, C: 5, D: 4, E: 3 }
export const SECTION_ORDER: Section[] = ['A', 'B', 'C', 'D', 'E']

export function getSectionQuestions(section: Section): number[] {
  const offsets: Record<Section, number> = { A: 0, B: 7, C: 15, D: 20, E: 24 }
  const start = offsets[section]
  return Array.from({ length: SECTION_SIZES[section] }, (_, i) => start + i)
}

const initialState: QuizState = {
  phase: 'email-capture',
  currentSection: 'A',
  currentQuestionIndex: 0,
  showSectionIntro: true,
  answers: new Map(),
  multiSelectBuffer: new Map(),
  email: '',
  firstName: '',
  resultId: null,
  sessionId: null,
  submitError: null,
}

function quizReducer(state: QuizState, action: QuizAction): QuizState {
  switch (action.type) {
    case 'START_HYDRATION':
      return { ...state, phase: 'hydrating' }

    case 'HYDRATION_FAILED':
      return { ...state, phase: 'email-capture' } // fallback to fresh quiz

    case 'HYDRATE_SESSION_STATE':
      return {
        ...state,
        ...action.payload,
        phase: action.payload.phase || 'quiz',
        answers: new Map(action.payload.answers || []),
        multiSelectBuffer: new Map(
          (action.payload.multiSelectBuffer || []).map(([k, v]: [string, string[]]) => [k, new Set(v)])
        )
      }

    case 'SET_SESSION_ID':
      return { ...state, sessionId: action.sessionId }

    case 'SET_EMAIL':
      return { ...state, email: action.email, firstName: action.firstName }

    case 'START_QUIZ':
      return {
        ...state,
        phase: 'quiz',
        currentSection: 'A',
        currentQuestionIndex: 0,
        showSectionIntro: true,
      }

    case 'DISMISS_SECTION_INTRO':
      return { ...state, showSectionIntro: false }

    case 'ANSWER': {
      const newAnswers = new Map(state.answers)
      newAnswers.set(action.questionId, {
        questionId: action.questionId,
        answerKey: action.answerKey,
        answerLabel: action.answerLabel,
      })
      return { ...state, answers: newAnswers }
    }

    case 'MULTI_SELECT_TOGGLE': {
      const buffer = new Map(state.multiSelectBuffer)
      const current = new Set(buffer.get(action.questionId) || [])

      if (action.answerKey === 'none') {
        current.clear()
        current.add('none')
      } else {
        current.delete('none')
        if (current.has(action.answerKey)) {
          current.delete(action.answerKey)
        } else {
          current.add(action.answerKey)
        }
      }

      buffer.set(action.questionId, current)
      return { ...state, multiSelectBuffer: buffer }
    }

    case 'MULTI_SELECT_CONFIRM': {
      const buffer = state.multiSelectBuffer.get(action.questionId)
      if (!buffer || buffer.size === 0) return state

      const keys = Array.from(buffer)
      const newAnswers = new Map(state.answers)
      newAnswers.set(action.questionId, {
        questionId: action.questionId,
        answerKey: keys.join(','),
        answerLabel: keys.join(','),
      })
      return { ...state, answers: newAnswers }
    }

    case 'NEXT_QUESTION': {
      const sectionSize = SECTION_SIZES[state.currentSection]
      const nextIndex = state.currentQuestionIndex + 1

      if (nextIndex < sectionSize) {
        return { ...state, currentQuestionIndex: nextIndex }
      }

      const sectionIdx = SECTION_ORDER.indexOf(state.currentSection)
      if (sectionIdx === SECTION_ORDER.length - 1) {
        return { ...state, phase: 'submitting' }
      }

      const nextSection = SECTION_ORDER[sectionIdx + 1]
      return {
        ...state,
        currentSection: nextSection,
        currentQuestionIndex: 0,
        showSectionIntro: true,
      }
    }

    case 'PREVIOUS_QUESTION': {
      if (state.currentQuestionIndex > 0) {
        return { ...state, currentQuestionIndex: state.currentQuestionIndex - 1 }
      }

      const sectionIdx = SECTION_ORDER.indexOf(state.currentSection)
      if (sectionIdx > 0) {
        const prevSection = SECTION_ORDER[sectionIdx - 1]
        return {
          ...state,
          currentSection: prevSection,
          currentQuestionIndex: SECTION_SIZES[prevSection] - 1,
          showSectionIntro: false,
        }
      }
      return state
    }

    case 'ENTER_SECTION':
      return {
        ...state,
        currentSection: action.section,
        currentQuestionIndex: 0,
        showSectionIntro: true,
      }

    case 'SUBMIT':
      return { ...state, phase: 'submitting', submitError: null }

    case 'RESULT_READY':
      return { ...state, phase: 'results', resultId: action.resultId }

    case 'SUBMIT_ERROR':
      return { ...state, phase: 'quiz', submitError: action.error }

    default:
      return state
  }
}

const LOCAL_STORAGE_KEY = 'femm_quiz_state'

export function useQuizState() {
  const [state, dispatch] = useReducer(quizReducer, initialState, (initial) => {
    try {
      if (typeof window !== 'undefined') {
        // If we have a URL param "?session=", we jump straight to hydrating
        const params = new URLSearchParams(window.location.search)
        if (params.get('session')) {
          return { ...initial, phase: 'hydrating' }
        }

        const saved = localStorage.getItem(LOCAL_STORAGE_KEY)
        if (saved) {
          const parsed = JSON.parse(saved)
          return {
            ...parsed,
            answers: new Map(parsed.answers || []),
            multiSelectBuffer: new Map(
              (parsed.multiSelectBuffer || []).map(([k, v]: [string, string[]]) => [k, new Set(v)])
            )
          }
        }
      }
    } catch (err) {
      console.error('Failed to parse saved quiz state', err)
    }
    return initial
  })

  // 1. Check URL for `session` Parameter
  useEffect(() => {
    if (typeof window === 'undefined') return
    const params = new URLSearchParams(window.location.search)
    const sessionIdUrl = params.get('session')

    if (sessionIdUrl && !state.sessionId && (state.phase === 'email-capture' || state.phase === 'hydrating')) {
      if (state.phase !== 'hydrating') dispatch({ type: 'START_HYDRATION' })
      fetch(`/api/session?id=${sessionIdUrl}`)
        .then(res => {
          if (!res.ok) throw new Error('Session not found')
          return res.json()
        })
        .then(payload => {
          dispatch({ type: 'HYDRATE_SESSION_STATE', payload })
        })
        .catch(() => {
          dispatch({ type: 'HYDRATION_FAILED' })
        })
    }
  }, [state.sessionId, state.phase])

  // 2. Persist to Local Storage + Server Side Sync
  useEffect(() => {
    if (state.phase === 'results' || state.phase === 'submitting') {
      localStorage.removeItem(LOCAL_STORAGE_KEY)
      // We could mark it completed on server, but /api/submit usually handles business logic
      if (state.sessionId && state.phase === 'results') {
        const serializable = {
          ...state,
          answers: Array.from(state.answers.entries()),
          multiSelectBuffer: Array.from(state.multiSelectBuffer.entries()).map(([k, v]) => [k, Array.from(v)])
        }
        fetch('/api/session', {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: state.sessionId, state_json: JSON.stringify(serializable), status: 'completed' })
        }).catch(() => {})
      }
    } else if (state.phase !== 'email-capture' && state.phase !== 'hydrating') {
      const serializable = {
        ...state,
        answers: Array.from(state.answers.entries()),
        multiSelectBuffer: Array.from(state.multiSelectBuffer.entries()).map(([k, v]) => [k, Array.from(v)])
      }
      const serializedJson = JSON.stringify(serializable)
      localStorage.setItem(LOCAL_STORAGE_KEY, serializedJson)

      // Post to Server if new session, otherwise Put to update
      if (!state.sessionId && state.email) {
        fetch('/api/session', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email: state.email, firstName: state.firstName, state_json: serializedJson })
        })
          .then(res => res.json())
          .then(data => {
            if (data.sessionId) dispatch({ type: 'SET_SESSION_ID', sessionId: data.sessionId })
          })
          .catch(console.error)
      } else if (state.sessionId) {
        fetch('/api/session', {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: state.sessionId, state_json: serializedJson, status: 'abandoned' })
        }).catch(() => {}) // fire and forget
      }
    }
  }, [state])

  return { state, dispatch }
}
